import * as React from 'react'
import { PublicationCode } from '@/scopes/common/domain/constants/publication-codes'
import { CatalogueType } from '@/scopes/common/domain/enums/CatalogueType'
import { PublicationCodes } from '@/scopes/common/domain/models'
import { SearchFilterType } from '@/scopes/search/search/infrastructure/api/types'
import { AccommodationTypeFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/AccommodationTypeFilter'
import { AirlineFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/AirlineFilter'
import { CampaignsAndDiscountsFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/CampaignsAndDiscountsFilter'
import { CollectionsFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/CollectionsFilter'
import { CustomerRatingFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/CustomerRatingFilter'
import { DestinationFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/DestinationFilter'
import { DistanceFilters } from '@/scopes/search/search/pages/SearchPage/components/Filters/DistanceFilters/DistanceFilters'
import { FacilitiesFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/FacilitiesFilter'
import { HasAvailabilityNextSeasonFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/HasAvailabilityNextSeasonFilter'
import { IntelligentFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/IntelligentFilter'
import { LastMinuteFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/LastMinuteFilter'
import { MealPlanFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/MealPlanFilter'
import { NumberOfRoomsFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/NumberOfRoomsFilter'
import { PricePerPersonFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/PricePerPersonFilter'
import { SkiAreaFilters } from '@/scopes/search/search/pages/SearchPage/components/Filters/SkiAreaFilters'
import { StarsFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/StarsFilter'
import { VacationTypeFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/VacationTypeFilter'
import { TransportTypeFilter } from '../../pages/SearchPage/components/Filters/TransportTypeFilter'

export type FilterComponent =
  | 'HasAvailabilityNextSeasonFilter'
  | 'IntelligentFilter'
  | 'DestinationFilter'
  | 'PricePerPersonFilter'
  | 'TransportTypeFilter'
  | 'AirlineFilter'
  | 'AccommodationTypeFilter'
  | 'MealPlanFilter'
  | 'StarsFilter'
  | 'CustomerRatingFilter'
  | 'DistanceFilters'
  | 'SkiAreaFilters'
  | 'FacilitiesFilter'
  | 'NumberOfRoomsFilter'
  | 'VacationTypeFilter'
  | 'CampaignsAndDiscountsFilter'
  | 'LastMinuteFilter'
  | 'CollectionsFilter'

export const FilterComponentMap: Record<FilterComponent, React.FC<{ defaultOpen?: boolean }>> = {
  HasAvailabilityNextSeasonFilter,
  IntelligentFilter,
  DestinationFilter,
  PricePerPersonFilter,
  TransportTypeFilter,
  AirlineFilter,
  AccommodationTypeFilter,
  MealPlanFilter,
  StarsFilter,
  CustomerRatingFilter,
  DistanceFilters,
  SkiAreaFilters,
  FacilitiesFilter,
  NumberOfRoomsFilter,
  VacationTypeFilter,
  CampaignsAndDiscountsFilter,
  LastMinuteFilter,
  CollectionsFilter,
}

interface FilterComponentConfiguration {
  filterComponent: FilterComponent
  relatedFilterTypes: SearchFilterType[]
  defaultOpen: boolean
}

export interface FilterFeatureOptions {
  isAirlineFilterEnabled?: boolean
}

const getFilterComponents: Record<CatalogueType, FilterComponentConfiguration[]> = {
  [CatalogueType.Summer]: [
    { filterComponent: 'IntelligentFilter', relatedFilterTypes: [], defaultOpen: true },
    { filterComponent: 'DestinationFilter', relatedFilterTypes: ['Country', 'Region', 'City'], defaultOpen: true },
    { filterComponent: 'PricePerPersonFilter', relatedFilterTypes: ['PricePerPerson'], defaultOpen: true },
    { filterComponent: 'TransportTypeFilter', relatedFilterTypes: ['TransportType'], defaultOpen: true },
    { filterComponent: 'AirlineFilter', relatedFilterTypes: ['Airline'], defaultOpen: true },
    { filterComponent: 'AccommodationTypeFilter', relatedFilterTypes: ['AccommodationType'], defaultOpen: true },
    { filterComponent: 'MealPlanFilter', relatedFilterTypes: ['Mealplan'], defaultOpen: true },
    {
      filterComponent: 'CampaignsAndDiscountsFilter',
      relatedFilterTypes: ['CampaignsAndDiscounts'],
      defaultOpen: false,
    },
    {
      filterComponent: 'FacilitiesFilter',
      relatedFilterTypes: ['Facilities', 'InternetFacilities', 'ChildrenFacilities'],
      defaultOpen: false,
    },
    { filterComponent: 'StarsFilter', relatedFilterTypes: ['Stars'], defaultOpen: false },
    {
      filterComponent: 'DistanceFilters',
      relatedFilterTypes: ['MetersToBeach', 'MetersToCenter'],
      defaultOpen: false,
    },
    { filterComponent: 'CustomerRatingFilter', relatedFilterTypes: ['CustomersRating'], defaultOpen: false },
    { filterComponent: 'VacationTypeFilter', relatedFilterTypes: ['Themes'], defaultOpen: false },
    { filterComponent: 'LastMinuteFilter', relatedFilterTypes: ['Lastminute'], defaultOpen: false },
    { filterComponent: 'NumberOfRoomsFilter', relatedFilterTypes: ['RoomCount'], defaultOpen: false },
    { filterComponent: 'CollectionsFilter', relatedFilterTypes: ['Collection'], defaultOpen: false },
  ],
  [CatalogueType.Winter]: [
    {
      filterComponent: 'HasAvailabilityNextSeasonFilter',
      relatedFilterTypes: ['HasAvailabilityNextSeason'],
      defaultOpen: true,
    },
    { filterComponent: 'IntelligentFilter', relatedFilterTypes: [], defaultOpen: true },
    {
      filterComponent: 'DestinationFilter',
      relatedFilterTypes: ['Country', 'Region', 'City', 'Subregion'],
      defaultOpen: true,
    },
    { filterComponent: 'TransportTypeFilter', relatedFilterTypes: ['TransportType'], defaultOpen: true },
    { filterComponent: 'AirlineFilter', relatedFilterTypes: ['Airline'], defaultOpen: true },
    { filterComponent: 'AccommodationTypeFilter', relatedFilterTypes: ['AccommodationType'], defaultOpen: true },
    { filterComponent: 'MealPlanFilter', relatedFilterTypes: ['Mealplan'], defaultOpen: true },
    { filterComponent: 'StarsFilter', relatedFilterTypes: ['Stars'], defaultOpen: true },
    { filterComponent: 'CustomerRatingFilter', relatedFilterTypes: ['CustomersRating'], defaultOpen: true },
    {
      filterComponent: 'DistanceFilters',
      relatedFilterTypes: ['MetersToCenter', 'MetersToSlope', 'MetersToSkiLift'],
      defaultOpen: true,
    },
    {
      filterComponent: 'SkiAreaFilters',
      relatedFilterTypes: ['SkiAreaMinimumAltitudeMeters', 'SkiAreaSlopeKms'],
      defaultOpen: false,
    },
    {
      filterComponent: 'FacilitiesFilter',
      relatedFilterTypes: [
        'Facilities',
        'InternetFacilities',
        'ChildrenFacilities',
        'BathroomCount',
        'BedsInLivingroom',
      ],
      defaultOpen: false,
    },
    { filterComponent: 'NumberOfRoomsFilter', relatedFilterTypes: ['RoomCount'], defaultOpen: false },
    { filterComponent: 'VacationTypeFilter', relatedFilterTypes: ['Themes'], defaultOpen: false },
    {
      filterComponent: 'CampaignsAndDiscountsFilter',
      relatedFilterTypes: ['CampaignsAndDiscounts'],
      defaultOpen: false,
    },
    { filterComponent: 'LastMinuteFilter', relatedFilterTypes: ['Lastminute'], defaultOpen: false },
    { filterComponent: 'PricePerPersonFilter', relatedFilterTypes: ['PricePerPerson'], defaultOpen: false },
    { filterComponent: 'CollectionsFilter', relatedFilterTypes: ['Collection'], defaultOpen: false },
  ],
  [CatalogueType.None]: [],
}

const quickSearchBarFilterTypes: SearchFilterType[] = ['DepartureDate', 'Duration', 'DepartureAirport']

function normaliseCatalogueType(catalogueType: CatalogueType): CatalogueType.Summer | CatalogueType.Winter {
  return catalogueType === CatalogueType.Winter ? CatalogueType.Winter : CatalogueType.Summer
}

function applyPublicationOverrides(
  configs: FilterComponentConfiguration[],
  publicationCode?: PublicationCode,
): FilterComponentConfiguration[] {
  const isFromEliza = publicationCode != null && PublicationCodes.isElizaPublicationCode(publicationCode)

  if (!isFromEliza)
    return configs

  return configs.map(config =>
    config.filterComponent === 'DestinationFilter'
      ? { ...config, relatedFilterTypes: config.relatedFilterTypes.filter(type => type !== 'City') }
      : config,
  )
}

function applyFeatureOverrides(
  configs: FilterComponentConfiguration[],
  { isAirlineFilterEnabled = false }: FilterFeatureOptions,
): FilterComponentConfiguration[] {
  if (isAirlineFilterEnabled)
    return configs

  return configs.filter(config => config.filterComponent !== 'AirlineFilter')
}

function getConfigurations(
  catalogueType: CatalogueType,
  publicationCode: PublicationCode | undefined,
  featureOptions: FilterFeatureOptions,
): FilterComponentConfiguration[] {
  const configs = getFilterComponents[normaliseCatalogueType(catalogueType)]
  return applyFeatureOverrides(applyPublicationOverrides(configs, publicationCode), featureOptions)
}

export function getFilterComponentsConfigurationsByCatalogueType(
  catalogueType: CatalogueType,
  publicationCode?: PublicationCode,
  featureOptions: FilterFeatureOptions = {},
): FilterComponentConfiguration[] {
  return getConfigurations(catalogueType, publicationCode, featureOptions)
}

export function getRelatedFilterTypesForCatalogueType(
  catalogueType: CatalogueType,
  isFlightOnly?: boolean,
  publicationCode?: PublicationCode,
  featureOptions: FilterFeatureOptions = {},
): SearchFilterType[] {
  const filterComponents = getConfigurations(catalogueType, publicationCode, featureOptions)
  const relatedFilterTypes = [
    ...quickSearchBarFilterTypes,
    ...filterComponents.flatMap(config => config.relatedFilterTypes),
    ...(isFlightOnly ? (['ArrivalAirport'] as SearchFilterType[]) : []),
  ]
  // Map "MetersToSkiLift" to "MetersToSkilift" for API compatibility
  return Array.from(new Set(relatedFilterTypes)).map(type =>
    type === 'MetersToSkiLift' ? 'MetersToSkilift' : type,
  ) as SearchFilterType[]
}
